import {
  getAppointmentDetails,
  getTokboxValues,
} from '@/queries/telehealth.queries';
import { redirectToPlumApp } from '@/utils';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from '@tanstack/react-query';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from './AppContext';
import toast from 'react-hot-toast';
import {
  EAppointmentState,
  EAppointmentStatus,
  ITokboxAppointmentType,
} from '@/types';
import useDisclosure from '@/hooks/useDisclosure';
import { FEATURE_FLAGS } from '@/constants';
import { Loader } from '@/components/Common/Layout/Loader';

interface NecessaryAppointmentDetailsType {
  showJoinCallButton: boolean;
  hasDoctorJoined?: boolean;
  doctorInfo: {
    firstName?: string;
    lastName?: string;
    imageUrl?: string;
    title?: string;
    specialties?: string[];
  };
  state?: EAppointmentState;
  status?: EAppointmentStatus;
}

interface TelehealthContextTypes {
  isAudioOn: boolean;
  toggleAudioOn: () => void;
  isVideoOn: boolean;
  toggleVideoOn: () => void;
  apiKey: string;
  sessionId: string;
  token: string;
  isTokboxDetailsLoaded: boolean;
  isTokboxError: boolean;
  tokboxError: Error | null;
  appointmentId: string | null;
  necessaryAppointmentDetails: NecessaryAppointmentDetailsType;
  refetchAppointmentDetails: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<ITokboxAppointmentType, Error>>;
  isRefetchingAppointmentDetails: boolean;
  isRefetchAppointmentDetailsError: boolean;
  redirectToFeedbackPage: () => void;
  navigateToCallPage: () => void;
  redirectToJoinCallPage: () => void;
}

export const TelehealthContext = React.createContext<
  TelehealthContextTypes | undefined
>(undefined);

const TelehealthContextProvider = ({ children }: React.PropsWithChildren) => {
  const { isOpen: isAudioOn, onToggle: toggleAudioOn } = useDisclosure();
  const { isOpen: isVideoOn, onToggle: toggleVideoOn } = useDisclosure();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const appointmentId = queryParams.get('appointmentId') ?? '';
  const navigate = useNavigate();

  const {
    data: appointmentDetails,
    error: appointmentDetailsError,
    refetch: refetchAppointmentDetails,
    isRefetchError: isRefetchAppointmentDetailsError,
    isRefetching: isRefetchingAppointmentDetails,
  } = useQuery({ ...getAppointmentDetails({ appointmentId }) });

  /**
   * We'll refetch the tokbox values to make sure the session hasn't expired
   */
  const {
    data,
    isLoading: isTokboxDetailsLoaded,
    isError: isTokboxError,
    error: tokboxError,
  } = useQuery({
    ...getTokboxValues({ appointmentId }),
    enabled: true,
    staleTime: 1000 * 60 * 30,
  });

  const redirectToFeedbackPage = () => {
    navigate(`/telehealth/feedback?appointmentId=${appointmentId}`);
  };

  const navigateToCallPage = () => {
    const url = `/telehealth?appointmentId=${appointmentId}`;
    navigate(url);
  };

  const redirectToJoinCallPage = () => {
    const url = `/telehealth/call?appointmentId=${appointmentId}`;
    navigate(url);
  };

  const { apiKey, sessionId, token } = data ?? {
    apiKey: '',
    sessionId: '',
    token: '',
  };

  const { userConfig, isAppLoading, isWebView, xPlumToken } = useAppContext();
  const { featureFlags } = userConfig || {};

  const isTelehealthEnabled = featureFlags?.filter(
    (feat) => feat.name === FEATURE_FLAGS.TELEHEALTH_WEB,
  )[0].enabled;

  const necessaryAppointmentDetails: NecessaryAppointmentDetailsType = {
    showJoinCallButton: appointmentDetails?.showJoinCallButton ?? false,
    doctorInfo: {
      firstName: appointmentDetails?.consultant.firstName,
      lastName: appointmentDetails?.consultant.lastName,
      imageUrl: appointmentDetails?.consultant.imageUrl,
      title: appointmentDetails?.consultant.title,
      specialties: appointmentDetails?.consultant.specialities,
    },
    state: appointmentDetails?.state,
    status: appointmentDetails?.status,
  };

  if (isAppLoading) {
    return <Loader />;
  }

  /**
   * In case user tries to paste link directly on their browser, we'll redirect
   * them to plum app
   */
  if ((isWebView || !xPlumToken) && !isAppLoading) {
    const redirectToUrl = encodeURI(window.location.href);
    redirectToPlumApp(`/logout?redirectTo=${redirectToUrl}`)();
    return null;
  }

  if (
    (!appointmentId || !isTelehealthEnabled || appointmentDetailsError) &&
    !isAppLoading
  )
    redirectToPlumApp('/benefits-revamp')();

  if (
    xPlumToken &&
    necessaryAppointmentDetails.state === EAppointmentState.COMPLETED
  ) {
    toast('The appointment has been completed', {
      style: {
        width: 'fit-content',
        maxWidth: '500px',
      },
      id: 'telehealth-invalid-appointment-toast',
    });
    setTimeout(() => {
      redirectToPlumApp('/benefits-revamp')();
    }, 2000);
  }

  if (
    xPlumToken &&
    necessaryAppointmentDetails.state === EAppointmentState.CANCELLED
  ) {
    toast('The appointment has been cancelled', {
      style: {
        width: 'fit-content',
        maxWidth: '500px',
      },
      id: 'telehealth-invalid-appointment-toast',
    });
    setTimeout(() => {
      redirectToPlumApp('/benefits-revamp')();
    }, 2000);
  }

  if (
    xPlumToken &&
    (isTokboxError ||
      necessaryAppointmentDetails.state === EAppointmentState.FAILED ||
      necessaryAppointmentDetails.status ===
        EAppointmentStatus.UNSUCCESSFUL_CALL)
  ) {
    toast('The appointment is not valid', {
      style: {
        width: 'fit-content',
        maxWidth: '500px',
      },
      id: 'telehealth-invalid-appointment-toast',
    });
    setTimeout(() => {
      redirectToPlumApp('/benefits-revamp')();
    }, 2000);
  }

  return (
    <TelehealthContext.Provider
      value={{
        isAudioOn,
        isVideoOn,
        toggleAudioOn,
        toggleVideoOn,
        apiKey,
        sessionId,
        token,
        isTokboxDetailsLoaded,
        isTokboxError,
        tokboxError,
        appointmentId,
        necessaryAppointmentDetails,
        refetchAppointmentDetails,
        isRefetchingAppointmentDetails,
        isRefetchAppointmentDetailsError,
        redirectToFeedbackPage,
        navigateToCallPage,
        redirectToJoinCallPage,
      }}
    >
      {children}
    </TelehealthContext.Provider>
  );
};

export default TelehealthContextProvider;
